<script setup lang="ts">
import DRBtnIcon from "@/components/UI/DRBtnIcon.vue";
import DRBtnPink from "@/components/UI/DRBtnPink.vue";
import DREditTextDialog from "@/components/UI/feedbacks/DREditTextDialog.vue";
import type { RulesProps } from "@/composable/useValidation";
import type { MetadataDetector, MetadataType } from "@/types";
import { computed, ref } from "vue";

const props = defineProps<{
  modelValue?: MetadataType;
  suggestion?: MetadataType;
  metadataDetector: MetadataDetector;
  placeholder?: string;
  rules?: RulesProps[];
  textarea?: boolean;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: MetadataType): void;
  (e: "blur"): void;
}>();

const value = computed({
  get: () => props.modelValue,
  set: (val: MetadataType) => emit("update:modelValue", val),
});

const editing = ref(false);
</script>

<template>
  <div class="d-flex flex-column gap-1">
    <!-- Le crayon reste collé au label : c'est lui qu'on modifie, pas la ligne -->
    <div class="d-flex flex-row gap-1 align-items-center">
      <span class="fw-medium">{{
        $t("report:diagnostic.metadata." + metadataDetector.slug)
      }}</span>

      <DRBtnIcon
        @click="editing = true"
        :size="26"
        icon="bi-pencil"
        variant="flat"
        class="edit-btn"
      />

      <div class="col-auto ms-auto" v-if="suggestion && suggestion !== value">
        <el-tooltip :content="suggestion" effect="light" placement="top">
          <DRBtnPink @click="value = suggestion" />
        </el-tooltip>
      </div>
    </div>

    <div class="d-flex flex-row gap-2 align-items-center text-description">
      <div v-if="value?.length" style="white-space: pre-line">{{ value }}</div>
      <div v-else class="fw-light">{{ $t("empty") }}</div>
    </div>
  </div>
  <DREditTextDialog
    v-model="editing"
    :title="metadataDetector.label"
    :placeholder="placeholder"
    :rules="rules"
    :value="value"
    :textarea="textarea"
    @confirm="value = $event"
  />
</template>

<style lang="scss" scoped>
/* Même traitement que le crayon de l'en-tête de page : gris, révélé au survol */
.edit-btn {
  opacity: 0.45;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 1;
  }
}
</style>
